import type { BillingWorkflowSelfServeCommandResponseBodyType } from "@intelligems/ig-types";

import { useHandleSelfServeCommand } from "@src/api/billing-workflow/self-serve-commands";
import { useGetModularLivePrices } from "@src/api/intelligems/get-live-prices";

// Entitlement profile keys included in the onboarding free trial: every currently
// sold monthly module except Smart Pricing, which the backend trial policy
// rejects. We select via an explicit allowlist instead
// of filtering Smart Pricing out of the full live-price feed, because that feed
// also carries archived SKUs (distinct "-ARCHIVED" keys), the mutually
// exclusive Starter/Unlimited bundle tiers, and an archived Smart Pricing key
// ("smart-pricing-t1-ARCHIVED") that slips past a key/name exclusion and gets
// the whole command rejected. An allowlist submits exactly the eligible prices.
export const TRIAL_INCLUDED_ENTITLEMENT_KEYS = new Set([
  "smart-content",
  "smart-merchandising",
  "smart-shipping",
  "smart-checkout-and-postpurchase",
]);

type StartTrialOptions = {
  onSuccess?: (data: BillingWorkflowSelfServeCommandResponseBodyType) => void;
};

export const useStartTrial = () => {
  const modularPrices = useGetModularLivePrices();
  const selfServeCommand = useHandleSelfServeCommand();

  // Quarterly and semi-annual module prices also carry interval "month" (interval_count
  // 3 and 6). Stripe rejects a subscription whose items mix interval_count, so the trial
  // takes only the true monthly price for each module.
  const trialPrices =
    modularPrices.data?.prices.filter(
      (p) =>
        p.interval === "month" &&
        p.intervalCount === 1 &&
        p.product.entitlementProfileKey != null &&
        TRIAL_INCLUDED_ENTITLEMENT_KEYS.has(p.product.entitlementProfileKey)
    ) ?? [];

  const trialPriceIds = trialPrices.flatMap((p) => p.priceIds);

  const startTrial = (options: StartTrialOptions = {}): boolean => {
    if (trialPriceIds.length === 0) {
      return false;
    }

    selfServeCommand.mutate(
      {
        action: "create",
        requestedProductSetTarget: {
          version: "2026_module_v1",
          priceIds: trialPriceIds,
        },
        requestedCadence: "monthly",
      },
      { onSuccess: options.onSuccess }
    );
    return true;
  };

  return {
    isError: selfServeCommand.isError,
    isModularPricesLoading: modularPrices.isLoading,
    isPending: selfServeCommand.isPending,
    startTrial,
    trialPriceIds,
    trialPrices,
  };
};
